/**
 * One fetch-state machine and one renderer for the whole console.
 *
 * `{data, loading, error, reload}` was written out by hand TEN times — in
 * `gateway/hooks/useGatewayAdmin.js` (deleted 2026-08-28), `useTenantBalance.js`, and as a
 * bare `useState` triplet inside each list tab. None of the ten recorded when the data
 * arrived, and every one of them rendered its error only `&& !rows`, so a failed RELOAD showed
 * nothing at all: stale rows stayed on screen looking authoritative. That is why the
 * silent-reload defect was ten bugs rather than one, and it bit hardest exactly where it
 * matters — the payout queue after an approve, payments after a refund, commission rules after
 * a save. As of 2026-08-28 every list on `GatewayConsole` renders through this file
 * (`GatewayConsole.staleRows.test.js` pins each one); `useTenantBalance.js` is the remaining
 * hand-rolled copy, on the tenant side.
 *
 * FOUR STATES, NOT THREE. The console's own best instinct is the reconciliation panel's
 * refusal to show a green "no drift" verdict for a run that never happened: *"it tells an
 * operator the books were checked and balanced when nothing was ever checked."* Generalised,
 * every surface must distinguish verified-good, verified-bad and NOT-KNOWN. `stale` is that
 * third state, made addressable.
 *
 * No polling, by decision. No `setInterval`, no `visibilitychange`, no focus refetch — and
 * do NOT reach for `swr` or `@tanstack/react-query` here even though both are already
 * dependencies: both revalidate on focus by default, which would repaint figures under an
 * operator's cursor while they decide whether to release someone's money. Freshness is
 * stated instead, and refreshing is theirs to ask for.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useT, useLanguage } from "../../lib/LanguageContext";
import { extractGatewayErrorCode } from "../../gateway/errors";
import { gatewayErrorText } from "../../gateway/errorText";
import { MUTED, RULE } from "./Ledger";
import { freshnessStamp } from "./stamp";

/**
 * @param {() => Promise<any>} fetcher  must be a stable callback (useCallback)
 * @returns {{data:any, error:any, code:string|null, loading:boolean, ranOnce:boolean,
 *            stale:boolean, fetchedAt:Date|null, reload:() => void}}
 */
export function useGatewayResource(fetcher) {
  const [state, setState] = useState({
    data: null, error: null, loading: true, ranOnce: false, fetchedAt: null,
  });
  // Monotonic, so a slow first response cannot overwrite a fast retry, and an unmount is a
  // no-op rather than a React warning.
  const seq = useRef(0);
  const alive = useRef(true);
  // RE-ARMED ON EVERY MOUNT, and that is the whole point. Written as
  // `useEffect(() => () => { alive.current = false; }, [])` this was a LATCH, not a
  // liveness check: `StrictMode` (src/index.js:50) mounts, runs the cleanup, and re-mounts,
  // so `alive` went false on the first cleanup and nothing ever set it back. Every
  // `.then(...)` below was then discarded, `loading` stayed true for ever, and the whole
  // redesigned overview sat on "reading…" over a ledger that had answered in 3 ms. Jest
  // does not wrap in StrictMode, so the unit tests were green throughout — which is why
  // `GatewayConsole.strictMode.test.js` now mounts inside one.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const reload = useCallback(() => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true }));
    Promise.resolve()
      .then(fetcher)
      .then((data) => {
        if (!alive.current || mine !== seq.current) return;
        // `fetchedAt` moves ONLY on success. Advancing it on a failed refresh would let the
        // strip claim a freshness it does not have.
        setState({ data, error: null, loading: false, ranOnce: true, fetchedAt: new Date() });
      })
      .catch((error) => {
        if (!alive.current || mine !== seq.current) return;
        // `data` is NOT cleared. Keeping it is what makes the stale state possible; the
        // renderer's job is to say the rows are old, not to hide them.
        setState((s) => ({ ...s, error, loading: false, ranOnce: true }));
      });
  }, [fetcher]);

  useEffect(() => { reload(); }, [reload]);

  return {
    ...state,
    code: extractGatewayErrorCode(state.error),
    stale: Boolean(state.error && state.data),
    reload,
  };
}

/** "as of 14:02", plus the one refresh control. Same control as the retry — designed once. */
export function Freshness({ resource, id, className = "" }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const { fetchedAt, loading, reload } = resource;
  return (
    <div className={`flex items-baseline gap-2 text-[11px] text-slate-400 tabular-nums ${className}`}
         data-testid={`${id}-freshness`}>
      <span>{fetchedAt ? t("gw.console.state.asOf", { at: freshnessStamp(fetchedAt, lang) })
                        : t("gw.console.state.neverRead")}</span>
      <span aria-hidden className="text-slate-300">·</span>
      <button
        type="button" onClick={reload} disabled={loading}
        className="text-slate-500 hover:text-slate-900 underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900 disabled:opacity-50"
        data-testid={`${id}-refresh`}>
        {loading ? t("gw.console.state.reading") : t("gw.console.state.refresh")}
      </button>
    </div>
  );
}


/**
 * The five-state renderer. `children` is a render-prop taking the loaded data, so no caller
 * re-checks null.
 *
 * `as="tbody"` because half the console's screens are tables, where `<tr><td colSpan>` is
 * required markup, and half are panels.
 */
export function ResourceState({ resource, id, as = "div", colSpan = 1, emptyText,
                               isEmpty, children }) {
  const t = useT("superadmin");
  const tc = useT("common");
  const { data, error, code, loading, ranOnce, stale } = resource;

  const empty = data != null && (isEmpty ? isEmpty(data) : Array.isArray(data) && data.length === 0);
  const Wrap = as === "tbody" ? "tbody" : "div";
  const cell = (node, testId, role) => (as === "tbody"
    ? <tr><td colSpan={colSpan} className="px-4 py-8 text-center" data-testid={testId} role={role}>{node}</td></tr>
    : <div className="px-4 py-8 text-center" data-testid={testId} role={role}>{node}</div>);

  // 1. First load. A placeholder rule rather than a spinner: it reserves the height, so
  //    nothing on the page jumps when the answer lands.
  if (loading && !ranOnce) {
    return <Wrap>{cell(<span className={`${MUTED} block`} aria-live="polite">{t("gw.console.state.reading")}</span>,
                       `${id}-loading`)}</Wrap>;
  }

  // 2. Hard failure — nothing to show. The ACTUAL refusal, not one generic sentence: a 403
  //    and a dropped connection used to read identically.
  if (error && data == null) {
    return (
      <Wrap>{cell(
        <div className="space-y-2">
          <p className="text-sm text-rose-700">{reason(code, tc, t)}</p>
          <Freshness resource={resource} id={id} className="justify-center" />
        </div>, `${id}-error`, "alert")}</Wrap>
    );
  }

  if (empty) {
    return <Wrap>{cell(<span className={MUTED}>{emptyText}</span>, `${id}-empty`)}</Wrap>;
  }

  const body = children(data);
  if (!stale) return <Wrap>{body}</Wrap>;

  // 4. STALE — the state the old console rendered as nothing at all. The rows stay, because
  //    they are the last thing we actually knew; what changes is that the screen says so.
  return (
    <Wrap>
      {cell(
        <div className="space-y-2" data-testid={`${id}-stale`}>
          <p className="text-sm text-rose-700">
            {t("gw.console.state.staleWarning", { reason: reason(code, tc, t) })}
          </p>
          <Freshness resource={resource} id={id} className="justify-center" />
        </div>, `${id}-stale-banner`, "alert")}
      {body}
    </Wrap>
  );
}

/** A code we can map, or an honest "the gateway did not answer" — never `gw.error.UNKNOWN`
 *  for a network drop, which tells the operator nothing they can act on. */
function reason(code, tc, t) {
  return code ? gatewayErrorText(code, tc) : t("gw.console.state.unreachable");
}

/** A write failure that STAYS on screen. Resolves its own sentence from the `common`
 *  namespace, so no caller can hand `gatewayErrorText` the wrong translator — the mistake
 *  that printed `gw.error.COMMISSION_RULE_CONFLICT` at three call sites. */
export function WriteError({ code, testId = "write-error" }) {
  const tc = useT("common");
  const t = useT("superadmin");
  if (code === undefined) return null;
  return (
    <p className="text-sm text-rose-700 mt-2" role="alert" data-testid={testId}>
      {reason(code, tc, t)}
    </p>
  );
}

export { RULE };
